import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { AuthShell } from "@/pages/Login";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords do not match."); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <AuthShell title="Invalid link" subtitle="This password reset link is missing or malformed.">
        <Link to="/forgot-password" className="text-[#D4AF37] hover:underline text-sm" data-testid="reset-goto-forgot">Request a new reset link</Link>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell title="Password updated" subtitle="Redirecting you to sign in…">
        <div className="flex items-center gap-3 text-emerald-400" data-testid="reset-success">
          <CheckCircle2 className="w-6 h-6" /> Your password has been reset successfully.
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Set a new password" subtitle="Choose a strong password for your account.">
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <div>
          <Label className="text-[#D4D4D8] mb-1.5 block">New password</Label>
          <Input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="reset-password-input" placeholder="At least 6 characters"
            className="bg-[#121214] border-white/10 text-white h-11" />
        </div>
        <div>
          <Label className="text-[#D4D4D8] mb-1.5 block">Confirm password</Label>
          <Input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
            data-testid="reset-confirm-input" placeholder="Re-enter your password"
            className="bg-[#121214] border-white/10 text-white h-11" />
        </div>
        {error && <p className="text-sm text-red-400" data-testid="reset-error">{error}</p>}
        <Button type="submit" disabled={loading} data-testid="reset-submit-btn"
          className="w-full fv-gold-gradient text-[#0A0A0B] font-semibold h-11 rounded-lg hover:opacity-90">
          {loading ? "Updating…" : "Reset password"}
        </Button>
      </form>
      <p className="text-center text-sm text-[#A1A1AA] mt-6">
        <Link to="/login" className="text-[#D4AF37] hover:underline" data-testid="reset-goto-login">Back to sign in</Link>
      </p>
    </AuthShell>
  );
}
